import React from "react";
import { ProductCardLabelWeightType } from "types/AssistantTypes";
import styles from "./informazioniGenerali.module.scss";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components/ui/tooltip";
import { QuestionMarkIcon } from "shared/assets/svg/QuestionMarkIcon";

type InformazioniGeneraliProps = {
  data: ProductCardLabelWeightType[];
  productType?: string;
};

export const InformazioniGenerali: React.FC<InformazioniGeneraliProps> = ({
  data,
  productType,
}) => {
  const { t } = useTranslation();

  const infoMap = React.useMemo(() => {
    return Object.fromEntries(
      data.map((item) => [
        item.label,
        { label: item.label, weight: item.weight },
      ]),
    );
  }, [data]);

  const renderBondInfo = () => (
    <div className={styles.infoGrid}>
      <div className={styles.column}>
        {infoMap["Rating (titoli)"] && (
          <p>
            {infoMap["Rating (titoli)"].label + ":"}
            <span>{infoMap["Rating (titoli)"].weight}</span>
          </p>
        )}
        {infoMap["YTM"] && (
          <p>
            {infoMap["YTM"].label + ":"}
            <span>{infoMap["YTM"].weight}</span>
          </p>
        )}
        {infoMap["Duration"] && (
          <p>
            {infoMap["Duration"].label + ":"}
            <span>{infoMap["Duration"].weight}</span>
          </p>
        )}
      </div>
      <div className={styles.column}>
        {infoMap["Spread"] && (
          <p>
            {infoMap["Spread"].label + ":"}
            <span>{infoMap["Spread"].weight}</span>
          </p>
        )}
        {infoMap["Prezzo secco"] && (
          <p>
            {infoMap["Prezzo secco"].label + ":"}
            <span>{infoMap["Prezzo secco"].weight}</span>
          </p>
        )}
        {infoMap["Prezzo tel quel"] && (
          <p>
            {infoMap["Prezzo tel quel"].label + ":"}
            <span>{infoMap["Prezzo tel quel"].weight}</span>
          </p>
        )}
      </div>
      <div className={styles.column}>
        {infoMap["Dividend yield"] && (
          <p>
            {infoMap["Dividend yield"].label + ":"}
            <span>{infoMap["Dividend yield"].weight}</span>
          </p>
        )}
        {infoMap["Ranking ESG"] && (
          <p>
            {infoMap["Ranking ESG"].label + ":"}
            <span>{infoMap["Ranking ESG"].weight}</span>
          </p>
        )}
      </div>
    </div>
  );

  const renderDefaultInfo = () => (
    <div className={styles.infoGrid}>
      <div className={styles.column}>
        {infoMap["ISIN"] ? (
          <p>
            {infoMap["ISIN"].label + ":"}
            <span>{infoMap["ISIN"].weight}</span>
          </p>
        ) : null}

        {infoMap["Categoria"] ? (
          <p>
            {infoMap["Categoria"].label + ":"}
            <span>{infoMap["Categoria"].weight}</span>
          </p>
        ) : null}

        {infoMap["Valuta"] ? (
          <p>
            {infoMap["Valuta"].label + ":"}
            <span>{infoMap["Valuta"].weight}</span>
          </p>
        ) : null}
      </div>
      <div className={styles.column}>
        {infoMap["Dividend Yield"] ? (
          <p>
            {infoMap["Dividend Yield"].label + ":"}
            <span>{infoMap["Dividend Yield"].weight}</span>
          </p>
        ) : null}

        {infoMap["Costi totali stimati*"] ? (
          <p>
            <span className={styles.labelWithTooltip}>
              {infoMap["Costi totali stimati*"].label.replace("*", "")}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={styles.infoIcon}>
                      <QuestionMarkIcon viewBox="0.5 0.5 17 17" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className={styles.tooltipContent}>
                    {t("productCard.costiTotalStimatiDescription")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <span className={styles.value}>
              {": " + infoMap["Costi totali stimati*"].weight}
            </span>
          </p>
        ) : null}

        {infoMap["ESG Classificazione SFDR"] ? (
          <p>
            {infoMap["ESG Classificazione SFDR"].label + ":"}
            <span>{infoMap["ESG Classificazione SFDR"].weight}</span>
          </p>
        ) : null}
      </div>
      <div className={styles.column}>
        {infoMap["Final Score"] ? (
          <p>
            {infoMap["Final Score"].label + ":"}
            <span>{infoMap["Final Score"].weight}</span>
          </p>
        ) : null}

        {infoMap["Final Score Advice"] ? (
          <p>
            {infoMap["Final Score Advice"].label + ":"}
            <span>{infoMap["Final Score Advice"].weight}</span>
          </p>
        ) : null}

        {infoMap["Ranking ESG"] ? (
          <p>
            {infoMap["Ranking ESG"].label + ":"}
            <span>{infoMap["Ranking ESG"].weight}</span>
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={styles.infoContainer}>
      <h2 className={styles.sectionTitle}>{t("productCard.mainInfo")}</h2>
      {productType === "BOND" ? renderBondInfo() : renderDefaultInfo()}
    </div>
  );
};

export default InformazioniGenerali;
